import { useState } from 'react'
import type { TextoAceite } from '../tipos'

/**
 * Etapa 6 — aceites.
 *
 * Distinção que a LGPD obriga e que a interface precisa refletir:
 *
 *  · ACEITE de negócio — a pessoa marca, e o servidor grava versão e hash.
 *    Hoje há um só: a Política de Devolução de Cliente.
 *  · O Aviso de Privacidade é AVISO, não pedido de permissão. A base legal é
 *    execução de contrato (art. 7º, V), não consentimento — que seria
 *    revogável no meio de uma análise de crédito. Por isso ele é exibido, e
 *    não tem caixa para marcar.
 *
 * QUAIS aceites aparecem NÃO está decidido aqui: a tela desenha o que o
 * `catalogo` devolver, e o servidor devolve o que estiver vigente. Tirar ou pôr
 * um item é encerrar ou reabrir a vigência no banco — sem tocar em código, e
 * preservando o registro de quem já aceitou a redação anterior.
 *
 * Marcar por engano é fácil; por isso cada aceite traz o link do documento ao
 * lado, e não uma linha de texto jurídico que ninguém lê.
 */

interface Props {
  textos: TextoAceite[]
  aceitos: string[]
  aoMudar: (chaves: string[]) => void
  aoVoltar: () => void
  aoAvancar: () => void
  ocupado: boolean
}

const AVISO = 'AvisoPrivacidade'

export function Aceites({ textos, aceitos, aoMudar, aoVoltar, aoAvancar, ocupado }: Props) {
  const [erro, setErro] = useState<string | null>(null)

  const exigidos = textos.filter((t) => t.chave !== AVISO)
  const aviso = textos.find((t) => t.chave === AVISO)

  function alternar(chave: string) {
    setErro(null)
    aoMudar(aceitos.includes(chave) ? aceitos.filter((c) => c !== chave) : [...aceitos, chave])
  }

  function seguir() {
    const faltando = exigidos.filter((t) => !aceitos.includes(t.chave))
    if (faltando.length) {
      setErro(
        faltando.length === 1
          ? `É necessário aceitar ${faltando[0]!.titulo} para prosseguir.`
          : 'É necessário aceitar todos os itens para prosseguir.',
      )
      return
    }
    aoAvancar()
  }

  return (
    <div>
      {/* Singular e plural conforme o que veio: "os itens abaixo" com um item
          só soa a formulário genérico, e formulário genérico é o que a pessoa
          marca sem ler. */}
      <p className="chamada">
        {exigidos.length === 1 ? 'Leia e aceite o item abaixo.' : 'Leia e aceite os itens abaixo.'}{' '}
        Guardamos a versão exata que você aceitou hoje — se o texto mudar depois, o seu aceite
        continua valendo para esta redação.
      </p>

      {exigidos.map((t) => (
        <label className="aceite" key={t.chave}>
          {/* Sempre DESMARCADO ao entrar na tela — nunca pré-selecionado.
              É opt-in por exigência do jurídico: caixa marcada por padrão não
              é manifestação de vontade, é omissão de quem não reparou. O estado
              inicial vem de `aceitos`, que nasce vazio. */}
          <input
            type="checkbox"
            className="aceite__caixa"
            checked={aceitos.includes(t.chave)}
            onChange={() => alternar(t.chave)}
          />
          <span>
            <span className="aceite__titulo">{t.titulo}</span>

            {/* Sem URL, o corpo é a própria declaração e aparece por extenso:
                não se declara sob as penas da lei o que não está à vista.
                Com URL, o corpo é um documento longo (PDF) e o link é o
                caminho certo — texto jurídico de dez páginas embutido na tela
                é o que faz a pessoa rolar sem ler. */}
            {t.corpo && !t.url ? (
              <span className="aceite__texto">{t.corpo}</span>
            ) : null}

            {t.url ? (
              <a
                className="elo"
                href={t.url}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
              >
                Abrir documento
              </a>
            ) : null}
            <span className="aceite__versao">Versão {t.versao}</span>
          </span>
        </label>
      ))}

      {aviso ? (
        <div className="nota">
          <strong>{aviso.titulo}</strong>
          {/* O texto vem do BANCO, não daqui. É a mesma regra dos aceites: o
              que a pessoa leu tem de ser o que está versionado e com hash, e
              não uma redação paralela dentro do React que ninguém revisa
              quando o jurídico muda a original. O fallback existe só para o
              caso de o corpo vir vazio. */}
          <p style={{ margin: '0.5rem 0 0' }}>
            {aviso.corpo?.trim() ||
              'Tratamos os dados informados para analisar seu cadastro e seu crédito, com base no artigo 7º, inciso V, da LGPD. Não é preciso consentir — é informação sobre como usamos os dados.'}
            {aviso.url ? (
              <>
                {' '}
                <a className="elo" href={aviso.url} target="_blank" rel="noopener noreferrer">
                  Ler na íntegra
                </a>
              </>
            ) : null}
          </p>
        </div>
      ) : null}

      {erro ? (
        <p className="aviso aviso--erro" role="alert">
          {erro}
        </p>
      ) : null}

      <div className="rodape">
        <button type="button" className="botao botao--secundario" onClick={aoVoltar}>
          Voltar
        </button>
        <span className="rodape__espaco" />
        <button type="button" className="botao botao--principal" onClick={seguir} aria-disabled={ocupado}>
          {ocupado ? 'Salvando…' : 'Continuar'}
        </button>
      </div>
    </div>
  )
}
